import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useUI } from "@/context/UIContext";
import { http, errorText, newToken, whatsappLink, MAILTO } from "@/lib/api";
import { EXAMPLES } from "@/content/site";

const EMPTY = { name: "", company: "", industry: "", email: "", phone: "", has_website: "", website: "", wishes: "", design_example: "", hp: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(f) {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Bitte geben Sie Ihren Namen an.";
  if (f.company.trim().length < 2) e.company = "Bitte geben Sie Ihr Unternehmen an.";
  if (f.industry.trim().length < 2) e.industry = "Bitte geben Sie Ihre Branche an.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
  if (f.phone.trim() && !/^[+0-9 ()/-]{6,30}$/.test(f.phone.trim())) e.phone = "Bitte nur Ziffern, Leerzeichen und + verwenden.";
  if (!f.has_website) e.has_website = "Bitte wählen Sie eine Option.";
  if (f.has_website === "renew" && f.website.trim().length < 4) e.website = "Bitte geben Sie Ihre bestehende Website an.";
  return e;
}

function Field({ id, label, error, optional, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 flex items-baseline justify-between text-[14px] font-semibold text-ink">
        {label}{optional && <span className="text-[12px] font-normal text-mute">optional</span>}
      </label>
      {children}
      {error && <p id={`${id}-err`} role="alert" className="mt-1.5 text-[13px] text-[#B42318]">{error}</p>}
    </div>
  );
}

const inputCls = (err) => `h-12 w-full rounded-xl border bg-white px-4 text-[15px] outline-none transition-colors focus:border-ink focus:ring-2 focus:ring-gold/40 ${err ? "border-[#B42318]" : "border-line"}`;

export default function DemoDialog() {
  const { demo, closeDemo, chosenExample } = useUI();
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");
  const token = useRef(newToken());
  const openedAt = useRef(Date.now());

  useEffect(() => {
    if (!demo.open) return;
    openedAt.current = Date.now();
    if (state === "done") { setF(EMPTY); setState("idle"); token.current = newToken(); }
    const ex = demo.example || chosenExample;
    if (ex) setF((p) => ({ ...p, design_example: ex }));
  }, [demo.open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => { setF((p) => ({ ...p, [k]: e.target.value })); if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined })); };
  const prop = (k) => ({ id: `demo-${k}`, value: f[k], onChange: set(k), "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `demo-${k}-err` : undefined, "data-testid": `demo-input-${k}` });

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    const v = validate(f);
    setErrors(v);
    if (Object.keys(v).length) { document.getElementById(`demo-${Object.keys(v)[0]}`)?.focus(); return; }
    setState("sending");
    setServerError("");
    try {
      const payload = { ...f, phone: f.phone || null, website: f.has_website === "renew" ? f.website : null, wishes: f.wishes || null, design_example: f.design_example || null, client_token: token.current, elapsed_ms: Date.now() - openedAt.current };
      const { data } = await http.post("/demo-requests", payload);
      if (data?.stored) setState("done"); else throw new Error("not stored");
    } catch (err) {
      setServerError(errorText(err, "Ihre Anfrage konnte gerade nicht gespeichert werden. Bitte versuchen Sie es erneut."));
      setState("error");
    }
  };

  return (
    <Dialog open={demo.open} onOpenChange={(o) => !o && closeDemo()}>
      <DialogContent data-testid="demo-dialog" className="max-h-[100dvh] w-full max-w-[640px] overflow-y-auto rounded-none border-line bg-paper p-0 sm:max-h-[92vh] sm:rounded-3xl">
        {state === "done" ? (
          <div className="p-8 sm:p-10" data-testid="demo-success">
            <CheckCircle2 className="h-12 w-12 text-gold-deep" />
            <DialogTitle className="mt-5 font-display text-[30px] font-semibold tracking-tight">Vielen Dank – Ihre Anfrage ist gespeichert.</DialogTitle>
            <DialogDescription className="mt-3 text-[16px] leading-relaxed text-mute">
              Wir melden uns mit Ihrer kostenlosen Website-Demo innerhalb von 24 Stunden – auch am Wochenende. Eine Eingangsbestätigung senden wir an {f.email}.
            </DialogDescription>
            <button type="button" onClick={closeDemo} data-testid="demo-success-close" className="btn btn-ink mt-8">Schließen</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="p-6 sm:p-10">
            <p className="eyebrow text-gold-deep">Kostenlos · innerhalb von 24 Stunden</p>
            <DialogTitle className="mt-3 font-display text-[28px] font-semibold leading-tight tracking-tight sm:text-[34px]">Ihre kostenlose Website-Demo</DialogTitle>
            <DialogDescription className="mt-2 text-[15px] text-mute">Wenige Angaben genügen. Die Demo ist ein erster individueller Eindruck – keine fertige Website.</DialogDescription>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <Field id="demo-name" label="Name" error={errors.name}><input {...prop("name")} autoComplete="name" maxLength={80} className={inputCls(errors.name)} /></Field>
              <Field id="demo-company" label="Unternehmen" error={errors.company}><input {...prop("company")} autoComplete="organization" maxLength={120} className={inputCls(errors.company)} /></Field>
              <Field id="demo-industry" label="Branche" error={errors.industry}><input {...prop("industry")} maxLength={80} placeholder="z. B. Tischlerei" className={inputCls(errors.industry)} /></Field>
              <Field id="demo-email" label="E-Mail" error={errors.email}><input {...prop("email")} type="email" autoComplete="email" inputMode="email" className={inputCls(errors.email)} /></Field>
              <Field id="demo-phone" label="Telefonnummer" optional error={errors.phone}><input {...prop("phone")} type="tel" autoComplete="tel" maxLength={30} className={inputCls(errors.phone)} /></Field>
              <Field id="demo-design_example" label="Gewähltes Designbeispiel" optional>
                <select {...prop("design_example")} className={inputCls(false)}>
                  <option value="">Kein bestimmtes</option>
                  {EXAMPLES.map((x) => <option key={x.key} value={x.name}>{x.name}</option>)}
                </select>
              </Field>
            </div>

            <fieldset className="mt-6" aria-describedby={errors.has_website ? "demo-has_website-err" : undefined}>
              <legend className="mb-2 text-[14px] font-semibold">Ihre Ausgangslage</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {[["none", "Ich habe noch keine Website"], ["renew", "Ich möchte meine Website erneuern"]].map(([v, l]) => (
                  <label key={v} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl border px-4 text-[14px] font-medium transition-colors ${f.has_website === v ? "border-ink bg-white" : "border-line bg-white/60"}`}>
                    <input type="radio" name="has_website" value={v} checked={f.has_website === v} onChange={set("has_website")} data-testid={`demo-radio-${v}`} id={v === "none" ? "demo-has_website" : undefined} className="h-4 w-4 accent-[#1A1C1E]" />
                    {l}
                  </label>
                ))}
              </div>
              {errors.has_website && <p id="demo-has_website-err" role="alert" className="mt-1.5 text-[13px] text-[#B42318]">{errors.has_website}</p>}
            </fieldset>

            {f.has_website === "renew" && (
              <div className="mt-5"><Field id="demo-website" label="Bestehende Website" error={errors.website}><input {...prop("website")} inputMode="url" placeholder="www.ihr-unternehmen.at" maxLength={200} className={inputCls(errors.website)} /></Field></div>
            )}
            <div className="mt-5">
              <Field id="demo-wishes" label="Kurze Wünsche" optional>
                <textarea {...prop("wishes")} rows={3} maxLength={1000} className="w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] outline-none focus:border-ink focus:ring-2 focus:ring-gold/40" />
              </Field>
            </div>
            <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="demo-hp">Bitte leer lassen</label><input id="demo-hp" tabIndex={-1} autoComplete="off" value={f.hp} onChange={set("hp")} />
            </div>

            {state === "error" && (
              <div role="alert" data-testid="demo-error" className="mt-6 flex gap-3 rounded-xl border border-[#B42318]/30 bg-[#FEF3F2] p-4 text-[14px] text-[#7A271A]">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{serverError} Ihre Eingaben bleiben erhalten. Alternativ erreichen Sie uns per <a className="font-semibold underline" href={whatsappLink(f.design_example)} target="_blank" rel="noopener noreferrer">WhatsApp</a> oder <a className="font-semibold underline" href={MAILTO}>E-Mail</a>.</span>
              </div>
            )}

            <p className="mt-6 text-[12px] leading-relaxed text-mute">Ihre Angaben verwenden wir ausschließlich zur Bearbeitung Ihrer Anfrage. Details in der <a href="/datenschutz" target="_blank" className="underline">Datenschutzerklärung</a>.</p>
            <button type="submit" disabled={state === "sending"} data-testid="demo-submit" className="btn btn-ink mt-5 w-full !min-h-[54px]">
              {state === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird gespeichert …</> : "Kostenlose Demo anfordern"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
